import PhoneUtils from '@wix/wixrest-utils/dist/PhoneUtils';
import Reservation from '@wix/wixrest-utils/dist/openrest-helpers/Reservation';
import { PropertiesUtils as OPU } from '@wix/wixrest-utils';
import Properties from '@wix/wixrest-properties';
import { EmailValidator } from 'commons-validator-js';
import _ from 'lodash';
import moment from 'moment-timezone';

const TYPES = {
    DATE: 'date',
    TIME: 'time',
    PARTY: 'party',
    FIRST_NAME: 'firstName',
    LAST_NAME: 'lastName',
    PHONE: 'phone',
    EMAIL: 'email',
    REQUESTS: 'requests',
    CUSTOM_FIELDS: 'customFields'
};

export default class ReservationValidator {
    constructor(restaurant) {
        this.restaurant = restaurant;
        this.countryCode = restaurant.address.countryCode;
        this.emailValidator = new EmailValidator();
    }

    validate(reservation, fullReservation = true) {
        const values = this._getValues(reservation, fullReservation);

        let isValid = _.compact(_.map(values, (val, type) => {
            let isValid = this._validateType(type, val, reservation);
            let error = null;

            if (isValid.error) {
                error = {
                    type,
                    error: isValid.reason
                };
            }

            return error;
        }));

        if (!isValid.length) {
            isValid = true;
        }
        return isValid;
    }

    _getValues(reservation, fullReservation) {
        const date = reservation.date;
        const time = reservation.time;
        const party = reservation.party;
        let values = {date, time, party};

        if (fullReservation) {
            const firstName = reservation.firstName;
            const lastName = reservation.lastName;
            const phone = reservation.phone;
            const email = reservation.email;
            const customFields = reservation.customFields;
            values = {...values, firstName, lastName, phone, email, customFields};
        }

        return values;
    }

    _validateType(type, value, reservation) {
        let isValid = false;
        value = _.isString(value) && value.trim() || value;

        switch (type) {
            case TYPES.DATE:
            case TYPES.TIME:
                isValid = this._validateDate(type, value, reservation);
                break;
            case TYPES.PARTY:
            case TYPES.FIRST_NAME:
            case TYPES.LAST_NAME:
                isValid = this._isNotEmpty(value);
                break;
            case TYPES.PHONE:
                isValid = this._isPhoneValid(value);
                break;
            case TYPES.EMAIL:
                isValid = this._isEmailValid(value);
                break;
            case TYPES.REQUESTS:
                isValid = true;
                break;
            case TYPES.CUSTOM_FIELDS:
                isValid = this._areCustomFieldsValid(value);
                break;
            default:
                throw new Error('Validator Error: unknown type');
        }

        return (isValid === true) || isValid;
    }

    _isNotEmpty(value) {
        let isNotEmpty = _.isNumber(value) || _.isDate(value) || !_.isEmpty(value);

        if (!isNotEmpty) {
            isNotEmpty = {error: true, reason: 'empty'};
        }

        return isNotEmpty;
    }

    _isPhoneValid(value) {
        let isValid = false;

        if (value) {
            isValid = !!PhoneUtils.validatePhone(value, this.countryCode);
        }

        if (!isValid) {
            isValid = {error: true, reason: 'badphone'}
        }

        return isValid;
    }

    _isEmailValid(value) {
        let isValid = false;

        if (value) {
            isValid = this.emailValidator.isValid(value);
        }

        if (!isValid) {
            isValid = {error: true, reason: 'bademail'};
        }

        return isValid;
    }

    _areCustomFieldsValid(customFields) {
        const customFieldsDefinition = OPU.get(this.restaurant.reservations, Properties.Reservations.CUSTOMFIELDS, 'com.wix.restaurants');

        let isValid = true;

        const errorIndexes = customFieldsDefinition && customFieldsDefinition.reduce((rc, field, index) => {
            if (field.required && !(customFields && customFields[index] && !_.isEmpty(customFields[index].value))) {
                rc.push(index);
            }
            return rc;
        }, []);

        if (errorIndexes && errorIndexes.length > 0) {
            isValid = {
                error: true,
                reason: errorIndexes.join(',')
            }
        }

        return isValid;
    }

    _validateDate(type, value, reservation) {
        let dateValue = type === 'date' ? value : reservation.date;
        let timeValue = type === 'time' ? value : reservation.time;
        let isValid = this._isNotEmpty(value);

        if (isValid === true && !(type === 'date' && !timeValue)) {
            const selectedDate = moment.tz(dateValue, this.restaurant.timezone).hour(timeValue.hour).minutes(timeValue.minutes);
            const available = Reservation.isValidReservation(this.restaurant, selectedDate);

            if ((this._isNotEmpty(dateValue) && !this._isNotEmpty(timeValue)) || available === true) {
                isValid = true;
            } else {
                if (available !== true) {
                    isValid = {
                        error: true,
                        reason: available.reason || available.status
                    }
                }
            }
        }

        return isValid;
    }
}
